import React, { useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { makeStyles } from "@material-ui/core/styles";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import api from "../../../../lib/api";
import { formatCurrency } from "../../../../lib/rateCardUtils";
import { getStripePaymentMethods } from "../../../Booking/queries";
import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import Overlay from "../../components/Overlay";

const useStyles = makeStyles({
  dialog: {
    width: "100%",
    maxWidth: 480,
    borderRadius: 12,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    padding: 24,
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },
  heading: { margin: "2px 0 0", fontSize: 20, fontWeight: 700, color: T.fg },
  closeBtn: {
    border: "none",
    background: "transparent",
    fontSize: 14,
    color: T.mutedFg,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  body: { marginTop: 16, display: "flex", flexDirection: "column", gap: 14 },
  summaryRows: { display: "flex", flexDirection: "column", gap: 8 },
  summary: {
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: T.surface,
    padding: "10px 12px",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "baseline",
  },
  summaryLabel: { fontSize: 12, color: T.mutedFg },
  amount: { fontSize: 18, fontWeight: 700, color: T.fg },
  note: { margin: 0, fontSize: 12, lineHeight: 1.45, color: T.mutedFg },
  success: {
    borderRadius: 6,
    border: `1px solid ${T.success}`,
    background: T.successTint,
    padding: "8px 10px",
    fontSize: 12,
    color: T.successText,
  },
  error: {
    borderRadius: 6,
    border: `1px solid ${T.danger}`,
    background: T.dangerTint,
    padding: "8px 10px",
    fontSize: 12,
    color: T.fg,
  },
  confirmBox: {
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    padding: "10px 12px",
  },
  footer: { marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 8 },
  cancelBtn: { border: "none", background: "transparent" },
});

export function bookingIdFor(booking) {
  return booking?.booking?.id || booking?.id || booking?.ref;
}

export function rateCardIdFor(booking) {
  return booking?.booking?.rateCardId || booking?.rateCardId || booking?.booking?.rateCard?.id;
}

export function scheduledAmount(details) {
  const raw = details?.billing?.scheduled?.fee?.total;
  const amount = raw == null ? 0 : Number(raw);
  return Number.isFinite(amount) ? amount : 0;
}

export function publishableKeyFor(appConfig, country) {
  const countryUpper = country ? country.toUpperCase() : null;
  return (
    (countryUpper === "US" && appConfig?.STRIPE_PUBLISHABLE_KEY_US) ||
    (countryUpper === "NZ" && appConfig?.STRIPE_PUBLISHABLE_KEY_NZ) ||
    appConfig?.STRIPE_PUBLISHABLE_KEY
  );
}

export function stripeCustomerIdFor(booking) {
  return booking?.stripeCustomerId || booking?.booking?.stripeCustomerId;
}

/* The hotel invoices this booking, so there is nothing to collect here. Read
 * off the row the tables already map, falling back to the rate card for a
 * caller that opens the dialog with a raw booking. */
export function isHotelInvoiced(booking) {
  if (booking?.paymentRequired === false) return true;

  return (
    (booking?.rateCard ?? booking?.booking?.rateCard)?.paymentStripe === false
  );
}

export function paymentMethodLabel(method) {
  const card = method?.card;
  if (!card?.last4) return "Saved payment method";
  return `${card.brand ? `${card.brand} ` : ""}•••• ${card.last4}`;
}

export default function TakePaymentDialog({
  appConfig,
  booking,
  country,
  installation,
  onClose,
  onPaid,
}) {
  const s = useStyles();
  const ui = useUi();
  const apolloClient = useApolloClient();
  const bookingId = bookingIdFor(booking);
  const rateCardId = rateCardIdFor(booking);
  const stripeCustomerId = stripeCustomerIdFor(booking);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [setupProcessing, setSetupProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmSavedCard, setConfirmSavedCard] = useState(false);
  const [savedPaymentMethod, setSavedPaymentMethod] = useState(null);
  const currency = installation?.currency || "AUD";
  const hotelInvoiced = isHotelInvoiced(booking);
  const totalFees = scheduledAmount(bookingDetails);
  /* On a chargeback card the guest owes the desk nothing; the same figure is
   * still priced, but it is what the hotel invoices rather than what is due. */
  const due = hotelInvoiced ? 0 : totalFees;
  const alreadyPaid = booking?.paid || booking?.paymentState?.isPaid;
  const isBusy = processing || setupProcessing;
  const stripeKey = publishableKeyFor(appConfig, country);
  const stripePromise = useMemo(() => stripeKey ? loadStripe(stripeKey) : null, [stripeKey]);
  const options = useMemo(
    () => ({ mode: "setup", currency: currency.toLowerCase() }),
    [currency],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadDetails() {
      if (!bookingId) {
        setError("Booking is required to take payment.");
        setLoading(false);
        return;
      }

      if (!installation?.id || !rateCardId) {
        setError("Booking details are required to take payment.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      setSuccess("");
      setConfirmSavedCard(false);
      setSavedPaymentMethod(null);

      async function createSetupIntent() {
        if (!stripeKey) {
          setError("Stripe is not configured for this installation.");
          setLoading(false);
          return;
        }

        const intent = await api.createValetSetupIntent(bookingId);
        if (cancelled) return;

        if (!intent?.client_secret) {
          setError("Unable to initialise payment setup. Please try again.");
          setLoading(false);
          return;
        }

        setClientSecret(intent.client_secret);
        setLoading(false);
      }

      try {
        const { data } = await api.bookingDetails({
          bookingId,
          installationId: installation.id,
          rateCardId,
        });
        if (cancelled) return;

        const details = data?.details ?? data;
        setBookingDetails(details);

        const outstanding = scheduledAmount(details);
        /* A chargeback booking stops here whatever it is priced at: there is no
         * card to take and the booking-pay route refuses it with a 422. */
        if (hotelInvoiced || alreadyPaid || outstanding <= 0) {
          setSuccess(
            hotelInvoiced
              ? "Nothing to collect - the hotel invoices this booking."
              : "No outstanding balance to charge.",
          );
          setLoading(false);
          onPaid?.(details);
          return;
        }

        if (stripeCustomerId) {
          const { data: paymentMethodsData } = await apolloClient.query({
            query: getStripePaymentMethods,
            variables: {
              customerId: stripeCustomerId,
              installationId: installation.id,
            },
            fetchPolicy: "no-cache",
          });
          if (cancelled) return;

          const method = paymentMethodsData?.getStripePaymentMethods?.[0];
          if (method) {
            setSavedPaymentMethod(method);
            setConfirmSavedCard(true);
            setLoading(false);
            return;
          }

          await createSetupIntent();
          return;
        }

        await createSetupIntent();
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Payment could not be initialised.");
          setLoading(false);
          setProcessing(false);
        }
      }
    }

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [alreadyPaid, apolloClient, bookingId, hotelInvoiced, installation?.id, onPaid, rateCardId, stripeCustomerId, stripeKey]);

  async function queueSavedCardPayment() {
    if (!bookingId || processing) return;

    setProcessing(true);
    setError("");

    try {
      const paymentResult = await api.queueBookingPayment(bookingId);
      setSuccess(paymentResult?.created ? "Payment queued." : "No payment needs to be queued.");
      setConfirmSavedCard(false);
      onPaid?.(paymentResult);
    } catch (err) {
      setError(err.message || "Payment could not be queued.");
    } finally {
      setProcessing(false);
    }
  }

  return (
    <Overlay onClose={isBusy ? undefined : onClose}>
      <div className={s.dialog}>
        <div className={s.head}>
          <div>
            <div className={ui.eyebrow}>Payment</div>
            <h3 className={s.heading}>
              {hotelInvoiced ? "Booking charges" : "Take booking payment"}
            </h3>
          </div>
          <button disabled={isBusy} onClick={onClose} className={s.closeBtn}>x</button>
        </div>

        <div className={s.body}>
          {bookingDetails && (
            <div className={s.summaryRows}>
              <div className={s.summary}>
                <span className={s.summaryLabel}>Amount due</span>
                <span className={s.amount}>{formatCurrency(due, currency)}</span>
              </div>
              {hotelInvoiced && (
                <div className={s.summary}>
                  <span className={s.summaryLabel}>Total Fees</span>
                  <span className={s.amount}>{formatCurrency(totalFees, currency)}</span>
                </div>
              )}
            </div>
          )}

          {loading ? <p className={s.note}>Checking payment status...</p> : null}
          {processing ? <p className={s.note}>Queueing payment from saved payment method...</p> : null}
          {success ? <div className={s.success}>{success}</div> : null}
          {error ? <div className={s.error}>{error}</div> : null}

          {!loading && !processing && !success && confirmSavedCard && (
            <div className={s.confirmBox}>
              <p className={s.note}>
                This guest already has {paymentMethodLabel(savedPaymentMethod)}
                on file. Confirm to queue the booking payment for the
                outstanding balance.
              </p>
              <div className={s.footer}>
                <button onClick={onClose} className={cx(ui.mutedBtn, s.cancelBtn)}>
                  Cancel
                </button>
                <button onClick={queueSavedCardPayment} className={ui.primaryBtn}>
                  Queue payment
                </button>
              </div>
            </div>
          )}

          {!loading && !processing && !success && !error && !confirmSavedCard && clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={options}>
              <SetupPaymentForm
                bookingId={bookingId}
                clientSecret={clientSecret}
                onProcessingChange={setSetupProcessing}
                onPaid={(view) => {
                  setSuccess(view?.created ? "Payment queued." : "No payment needs to be queued.");
                  onPaid?.(view);
                }}
              />
            </Elements>
          )}
        </div>

        {(success || error) && (
          <div className={s.footer}>
            <button onClick={onClose} className={cx(ui.mutedBtn, s.cancelBtn)}>
              Close
            </button>
          </div>
        )}
      </div>
    </Overlay>
  );
}

export function SetupPaymentForm({ bookingId, clientSecret, onPaid, onProcessingChange }) {
  const s = useStyles();
  const ui = useUi();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cardSaved, setCardSaved] = useState(false);

  useEffect(() => () => onProcessingChange(false), [onProcessingChange]);

  async function submit() {
    if (!stripe || !elements || loading) return;

    setLoading(true);
    onProcessingChange(true);
    setError("");

    if (cardSaved) {
      await queuePayment();
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError("Please enter valid card details.");
      setLoading(false);
      onProcessingChange(false);
      return;
    }

    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (confirmError) {
      setError("Invalid payment method. Please enter valid card details.");
      setLoading(false);
      onProcessingChange(false);
      return;
    }

    setCardSaved(true);
    await queuePayment();
  }

  async function queuePayment() {
    try {
      const paymentResult = await api.queueBookingPayment(bookingId);
      onPaid(paymentResult);
    } catch (err) {
      setError(err.message || "Card was saved, but the booking payment could not be queued.");
    } finally {
      setLoading(false);
      onProcessingChange(false);
    }
  }

  return (
    <div>
      {error ? <div className={s.error}>{error}</div> : null}
      {cardSaved ? (
        <div className={s.confirmBox}>
          <p className={s.note}>
            Card saved. Queue the booking payment for the outstanding balance.
          </p>
        </div>
      ) : (
        <PaymentElement />
      )}
      <div className={s.footer}>
        <button onClick={submit} disabled={loading || !stripe || !elements} className={ui.primaryBtn}>
          {loading ? "Processing..." : cardSaved ? "Queue payment" : "Save card & queue payment"}
        </button>
      </div>
    </div>
  );
}
